import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useContext, useEffect, useState } from 'react';
import { Col, Container, Modal, Row } from 'react-bootstrap';
import Button from '../../../components/Button';
import CalloutCard from '../../../components/CalloutCard';
import CircledIcon from '../../../components/CircledIcon';
import DatePickerInput from '../../../components/DatePicker/DatePicker';
import Tabs from '../../../components/Tabs/Tabs';
import Blockquote from '../../../components/Blockquote';
import { EnrollmentStore } from '../../../providers/enrollment';
import { openExternalLink } from '../../../utils/utils';
import HealthBenefitsTab from '../BenefitOptionsAndCostsModal/HealthBenefitsTab';
import LifeAndDisabilityInsuranceTab from '../BenefitOptionsAndCostsModal/LifeAndDisabilityInsuranceTab';
import LifestyleBenefitsTab from '../BenefitOptionsAndCostsModal/LifestyleBenefitsTab';
import SpendingAccountsTab from '../BenefitOptionsAndCostsModal/SpendingAccountsTab';

const calloutCardBtns = [
  <Button
    className={'col-12'}
    variant={'secondary-outline'}
    rightIcon={light('scale-balanced')}
    onClick={() => openExternalLink('https://uat.thconsumeradvantage.com/informedenrollment2/demo')}
  >
    Compare Plans
  </Button>,


  <Button
    className={'col-12'}
    variant={'secondary-outline'}
    rightIcon={light('user-doctor')}
    onClick={() =>
      openExternalLink(
        'https://qabtr.lifeatworkportal.com/dst/findprovider/?dataoverride=1'
      )
    }
  >
    Find a Provider
  </Button>,
];

export type CostAndCoverageModalProps = React.PropsWithChildren<{
  showModal: boolean;
  handleCloseModal: () => void;
}>;

const CostAndCoverageModal = ({
  showModal,
  handleCloseModal,
}: CostAndCoverageModalProps) => {
  const [isPreviewed, setPreview] = useState(false);

  const {
    activeHealthBenefits,
    activeSpendingAccounts,
    activeLifeAndDisabilityInsurances,
    activeLifestyleBenefits,
  } = useContext(EnrollmentStore);

  const tabsData = [
    {
      name: 'Health Benefits',
      content: <HealthBenefitsTab headerTitle={'Health Benefits'} healthBenefits={activeHealthBenefits}/>,
    },
    {
      name: 'Spending Accounts',
      content: <SpendingAccountsTab headerTitle={'Spending Accounts'} spendingAccounts={activeSpendingAccounts}/>,
    },
    {
      name: 'Life & Disability Insurance',
      content: <LifeAndDisabilityInsuranceTab headerTitle={'Life & Disability Insurance'} lifeAndDisabilityInsurances={activeLifeAndDisabilityInsurances}/>,
    },
    {
      name: 'Lifestyle Benefits',
      content: <LifestyleBenefitsTab headerTitle={'Lifestyle Benefits'} lifestyleBenefits={activeLifestyleBenefits}/>,
    },
  ];

  const [value, setValue] = useState<any>();

  const handlePreviewCostAndCoverage = () => {
    setPreview((prev) => !prev);
  };

  const handleSetValue = (e: any) => {
    setValue((prev: any) => (prev = e));
  };

  useEffect(() => {
    if (!showModal) {
      setPreview(false);
    }
  }, [showModal]);

  return (
    <Modal size={'xl'} show={showModal} onHide={handleCloseModal}>
      <Container className={'p-7 p-lg-12'}>
        <Modal.Header className={'border-0 ms-0 me-0 pt-0 pb-12'} closeButton>
          <Modal.Title className={'h6 fw-normal'}>
            <Row>
              <Col className={'d-flex align-items-center'}>
                <CircledIcon
                  variant="icon-emphasized"
                  icon={light('envelope-open-dollar')}
                />
                <h4 className={'ms-7 mb-0 text-sm-20-lg-32'}>
                  Preview Cost & Coverage
                </h4>
              </Col>
            </Row>
          </Modal.Title>
        </Modal.Header>

        <Row>
          <Col className={'mb-10 col-12 col-lg-8'}>
            <DatePickerInput
              maxWidth={'313px'}
              onChange={handleSetValue}
              placeholder={'Select a date'}
              label={'Enter the date your new child was born or adopted'}
              isValid={value !== undefined}
            />
          </Col>
        </Row>
        <Row className={'mb-10'}>
          <Col className={'col-12 col-lg-5'}>
            <Button
              disabled={!value}
              className={'col-12'}
              size={'lg'}
              onMouseDown={handlePreviewCostAndCoverage}
            >
              Preview Cost & Coverage
            </Button>
          </Col>
        </Row>

        {isPreviewed ? (
          <>
            <CalloutCard
              cardIcon={light('screwdriver-wrench')}
              buttons={calloutCardBtns}
            >
              <Row>
                <span className="bolder-text mb-5">
                  Would you like to explore your plan options in more detail?
                </span>
                <p>
                  <span className="bolder-text">Compare Plans</span> to check
                  coverage levels, costs and what plan is best for you and your
                  family
                </p>
                <p>
                  <span className="bolder-text">Find a provider</span> to see if
                  your doctor is included in your available plans.
                </p>
              </Row>
            </CalloutCard>
            <Container>
              <div className={'d-none d-lg-block mt-10'}>
                <Blockquote variant="primary">
                  <div className={'mt-2 text-ligther-grey'}>
                    <b>Important: </b>If you don’t make a different election, you’ll receive the highlighted coverage. 
                  </div>
                  <div className='mt-2'>
                    <b>Note</b>
                    <ul>
                      <li>This worksheet is for planning purposes only.</li>
                      <li>Coverage eligibility may change depending on the actual date of the event.</li>
                      <li>After you make new elections on the ‘Enroll in Benefits’ step, your cost for coverage may change.</li>
                    </ul>
                  </div>
                </Blockquote>
              </div>
            </Container>
            <Container
              className={'col-12 text-end px-0 mt-10'}
            >
              <a
                className={'text-decoration-none'}
                href={`${process.env.PUBLIC_URL}/${'blank-demo-example.pdf'}`}
                target={'_blank'}
                rel="noreferrer"
              >
                <Button
                  leftIcon={light('file-arrow-down')}
                  variant="primary"
                  className={'order-1 order-lg-3 mt-7 mt-lg-0'}
                  size="sm"
                >
                  Download/Print
                </Button>
              </a>
            </Container>
            <Container className={'px-0 mt-10'}>
              <Tabs tabs={tabsData} />
            </Container>
          </>
        ) : (
          <></>
        )}
        <Modal.Footer>
          <Button
            variant="primary-outline"
            onMouseDown={handleCloseModal}
            className="mt-7 mt-lg-0 order-1"
          >
            Close
          </Button>
        </Modal.Footer>
      </Container>
    </Modal>
  );
};

export default CostAndCoverageModal;
